import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SecurityService } from '../../services/security.service';
import {
  UserSession,
  SecurityEvent,
  UserActivity,
  SecurityDashboard,
  SecurityEventType,
  EventSeverity
} from '../../models/security.models';
import { Subject, interval } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { PageHeaderComponent } from '../../shared/ui/components';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmCard } from '@spartan-ng/helm/card';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { HlmIcon } from '@spartan-ng/helm/icon';
import {
  lucideRefreshCw, lucideUsers, lucideCheck, lucideLock, lucideAlertTriangle,
  lucideShieldAlert, lucideActivity, lucideBarChart2, lucideClock, lucideChevronDown,
} from '@ng-icons/lucide';

@Component({
  selector: 'app-security-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, PageHeaderComponent, HlmButton, HlmCard, NgIcon, HlmIcon],
  providers: [provideIcons({
    lucideRefreshCw, lucideUsers, lucideCheck, lucideLock, lucideAlertTriangle,
    lucideShieldAlert, lucideActivity, lucideBarChart2, lucideClock, lucideChevronDown,
  })],
  templateUrl: './security-dashboard.component.html',
  styleUrls: ['./security-dashboard.component.css']
})
export class SecurityDashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  // Dashboard data
  dashboardData: SecurityDashboard | null = null;
  activeSessions: UserSession[] = [];
  recentEvents: SecurityEvent[] = [];
  recentActivity: UserActivity[] = [];
  sessionMetrics: any = null;
  
  // Loading states
  loading = false;
  sessionsLoading = false;
  eventsLoading = false;
  activityLoading = false;
  metricsLoading = false;
  
  // Auto-refresh settings
  autoRefresh = true;
  refreshInterval = 30; // seconds
  
  // Session management
  selectedSessions: Set<string> = new Set();
  showSessionDetails = false;
  
  // Enhanced filtering
  sessionFilters = {
    ipAddress: '',
    userAgent: '',
    showSuspiciousOnly: false,
    timeRange: '24h' // 1h, 24h, 7d, 30d
  };
  
  // Event type filter options
  eventTypes = ['AccountLocked', 'SuspiciousActivity', 'PasswordChanged', 'UnauthorizedAccess'];
  severityLevels = ['Critical', 'High', 'Medium', 'Low'];
  
  // Current filters
  selectedEventType: string | 'all' = 'all';
  selectedSeverity: string | 'all' = 'all';

  constructor(private securityService: SecurityService) {}

  ngOnInit(): void {
    this.loadDashboardData();
    this.loadActiveSessions();
    this.loadRecentEvents();
    this.loadRecentActivity();
    this.loadSessionMetrics();
    
    // Set up auto-refresh
    if (this.autoRefresh) {
      this.startAutoRefresh();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Load dashboard overview data
  loadDashboardData(): void {
    this.loading = true;
    this.securityService.getSecuritySummary()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          // Map security summary to dashboard data structure
          this.dashboardData = {
            totalUsers: data.totalUsers || 0,
            activeUsers: data.activeSessions || 0,
            activeSessions: data.activeSessions || 0,
            recentLogins: data.successfulLogins || 0,
            failedLogins: data.failedLogins || 0,
            suspiciousActivities: data.securityEvents || 0,
            criticalEvents: data.criticalEvents || 0,
            systemHealth: 'healthy' as 'healthy' | 'warning' | 'critical',
            lastUpdated: new Date().toISOString()
          };
          this.loading = false;
        },
        error: (error: any) => {
          console.error('Failed to load dashboard data:', error);
          this.loading = false;
        }
      });
  }

  // Load active user sessions
  loadActiveSessions(): void {
    this.sessionsLoading = true;
    this.securityService.getActiveSessions({ isActive: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (sessions: any) => {
          this.activeSessions = sessions || [];
          this.sessionsLoading = false;
        },
        error: (error: any) => {
          console.error('Failed to load active sessions:', error);
          this.sessionsLoading = false;
        }
      });
  }

  // Load recent security events
  loadRecentEvents(): void {
    this.eventsLoading = true;
    const filters: any = { pageSize: 10 };
    
    if (this.selectedEventType !== 'all') {
      filters.eventType = this.selectedEventType;
    }
    if (this.selectedSeverity !== 'all') {
      filters.severity = this.selectedSeverity;
    }
    
    this.securityService.getSecurityEvents(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.recentEvents = response.items || [];
          this.eventsLoading = false;
        },
        error: (error: any) => {
          console.error('Failed to load security events:', error);
          this.eventsLoading = false;
        }
      });
  }

  // Load recent user activity
  loadRecentActivity(): void {
    this.activityLoading = true;
    this.securityService.getUserActivities({ page: 1, pageSize: 10 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.recentActivity = response.items || [];
          this.activityLoading = false;
        },
        error: (error: any) => {
          console.error('Failed to load user activity:', error);
          this.activityLoading = false;
        }
      });
  }

  // Start auto-refresh timer
  startAutoRefresh(): void {
    interval(this.refreshInterval * 1000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.securityService.getSecuritySummary())
      )
      .subscribe({
        next: (data: any) => {
          // Map security summary to dashboard data structure
          this.dashboardData = {
            totalUsers: data.totalUsers || 0,
            activeUsers: data.activeSessions || 0,
            activeSessions: data.activeSessions || 0,
            recentLogins: data.successfulLogins || 0,
            failedLogins: data.failedLogins || 0,
            suspiciousActivities: data.securityEvents || 0,
            criticalEvents: data.criticalEvents || 0,
            systemHealth: 'healthy' as 'healthy' | 'warning' | 'critical',
            lastUpdated: new Date().toISOString()
          };
        },
        error: (error: any) => {
          console.error('Auto-refresh failed:', error);
        }
      });
  }

  // Toggle auto-refresh
  toggleAutoRefresh(): void {
    this.autoRefresh = !this.autoRefresh;
    if (this.autoRefresh) {
      this.startAutoRefresh();
    }
  }

  // Manual refresh all data
  refreshAll(): void {
    this.loadDashboardData();
    this.loadActiveSessions();
    this.loadRecentEvents();
    this.loadRecentActivity();
    this.loadSessionMetrics();
  }

  // Load session metrics
  loadSessionMetrics(): void {
    this.metricsLoading = true;
    this.securityService.getSessionMetrics()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (metrics: any) => {
          this.sessionMetrics = metrics;
          this.metricsLoading = false;
        },
        error: (error: any) => {
          console.error('Failed to load session metrics:', error);
          this.metricsLoading = false;
          // Set default values if API fails
          this.sessionMetrics = {
            totalActiveSessions: 0,
            uniqueActiveUsers: 0,
            suspiciousSessions: 0,
            peakSessionsToday: 0,
            averageSessionDuration: 0
          };
        }
      });
  }

  // Enhanced session management
  toggleSessionSelection(sessionId: string): void {
    if (this.selectedSessions.has(sessionId)) {
      this.selectedSessions.delete(sessionId);
    } else {
      this.selectedSessions.add(sessionId);
    }
  }

  selectAllSessions(): void {
    this.activeSessions.forEach(session => {
      this.selectedSessions.add(session.id);
    });
  }

  clearSessionSelection(): void {
    this.selectedSessions.clear();
  }

  bulkInvalidateSessions(): void {
    if (this.selectedSessions.size === 0) return;
    
    if (confirm(`Are you sure you want to invalidate ${this.selectedSessions.size} selected sessions?`)) {
      const sessionIds = Array.from(this.selectedSessions);
      this.securityService.invalidateMultipleSessions(sessionIds)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            alert('Selected sessions invalidated successfully');
            this.clearSessionSelection();
            this.loadActiveSessions();
          },
          error: (error: any) => {
            console.error('Failed to invalidate sessions:', error);
            alert('Failed to invalidate selected sessions');
          }
        });
    }
  }

  flagSessionAsSuspicious(sessionId: string): void {
    const reason = prompt('Please provide a reason for flagging this session as suspicious:');
    if (reason && reason.trim()) {
      this.securityService.flagSuspiciousSession(sessionId, reason.trim())
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            alert('Session flagged successfully');
            this.loadRecentEvents(); // Reload events to show the new security event
          },
          error: (error: any) => {
            console.error('Failed to flag session:', error);
            alert('Failed to flag session');
          }
        });
    }
  }

  // Enhanced session filtering
  getFilteredSessions(): UserSession[] {
    return this.activeSessions.filter(session => {
      // IP Address filter
      if (this.sessionFilters.ipAddress && 
          !session.ipAddress.toLowerCase().includes(this.sessionFilters.ipAddress.toLowerCase())) {
        return false;
      }
      
      // User Agent filter
      if (this.sessionFilters.userAgent && 
          !session.userAgent.toLowerCase().includes(this.sessionFilters.userAgent.toLowerCase())) {
        return false;
      }
      
      // Suspicious sessions filter (this would require backend support)
      if (this.sessionFilters.showSuspiciousOnly) {
        // You would need to add a 'suspicious' flag to UserSession model
        // return session.isSuspicious;
      }
      
      // Time range filter
      if (this.sessionFilters.timeRange !== 'all') {
        const now = new Date();
        const sessionTime = new Date(session.createdAtUtc);
        const diffHours = (now.getTime() - sessionTime.getTime()) / (1000 * 60 * 60);
        
        switch (this.sessionFilters.timeRange) {
          case '1h':
            if (diffHours > 1) return false;
            break;
          case '24h':
            if (diffHours > 24) return false;
            break;
          case '7d':
            if (diffHours > 24 * 7) return false;
            break;
          case '30d':
            if (diffHours > 24 * 30) return false;
            break;
        }
      }
      
      return true;
    });
  }

  // Device detection helper
  getDeviceInfo(userAgent: string): { device: string; browser: string; os: string } {
    const device = this.detectDevice(userAgent);
    const browser = this.detectBrowser(userAgent);
    const os = this.detectOS(userAgent);
    
    return { device, browser, os };
  }

  private detectDevice(userAgent: string): string {
    if (/Mobile|Android|iPhone|iPad/.test(userAgent)) return 'Mobile';
    if (/Tablet/.test(userAgent)) return 'Tablet';
    return 'Desktop';
  }

  private detectBrowser(userAgent: string): string {
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('Opera')) return 'Opera';
    return 'Unknown';
  }

  private detectOS(userAgent: string): string {
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac OS')) return 'macOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS')) return 'iOS';
    return 'Unknown';
  }

  // Geographic location detection (if IP geolocation is available)
  getLocationFromIP(ipAddress: string): string {
    // This would require integration with an IP geolocation service
    // For now, return placeholder
    return 'Unknown Location';
  }

  // Security risk assessment
  getSessionRiskLevel(session: UserSession): 'low' | 'medium' | 'high' {
    let riskScore = 0;
    
    // Check for unusual IP patterns
    if (this.isUnusualIP(session.ipAddress)) riskScore += 2;
    
    // Check session age
    const sessionAge = new Date().getTime() - new Date(session.createdAtUtc).getTime();
    const hoursOld = sessionAge / (1000 * 60 * 60);
    if (hoursOld > 24) riskScore += 1;
    
    // Check for unusual user agent
    if (this.isUnusualUserAgent(session.userAgent)) riskScore += 2;
    
    // Check last activity
    if (session.lastActivityAtUtc) {
      const lastActivity = new Date().getTime() - new Date(session.lastActivityAtUtc).getTime();
      const inactiveHours = lastActivity / (1000 * 60 * 60);
      if (inactiveHours > 12) riskScore += 1;
    }
    
    if (riskScore >= 4) return 'high';
    if (riskScore >= 2) return 'medium';
    return 'low';
  }

  getSessionCardClass(session: UserSession): string {
    const risk = this.getSessionRiskLevel(session);
    if (risk === 'high') return 'border-red-200 bg-red-50/30';
    if (risk === 'medium') return 'border-amber-200 bg-amber-50/30';
    return 'border-border';
  }

  getSessionRiskBadgeClass(session: UserSession): string {
    const risk = this.getSessionRiskLevel(session);
    if (risk === 'high') return 'bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-400';
    if (risk === 'medium') return 'bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-400';
    return 'bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400';
  }

  getEventSeverityBadgeClass(severity: string): string {
    if (severity === 'Critical' || severity === 'High') return 'bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-400';
    if (severity === 'Medium') return 'bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-400';
    return 'bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400';
  }

  getEventResolvedBadgeClass(isResolved: boolean): string {
    if (isResolved) return 'bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400';
    return 'bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-400';
  }

  getActivitySuccessBadgeClass(success: boolean): string {
    if (success) return 'bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400';
    return 'bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-400';
  }

  getFailedLoginIconClass(count: number): string {
    if (count > 10) return 'bg-amber-50 dark:bg-amber-500/15 text-amber-700';
    return 'bg-stone-100 dark:bg-stone-700 text-stone-600 dark:text-stone-300';
  }

  getFailedLoginValueClass(count: number): string {
    if (count > 10) return 'text-amber-700 dark:text-amber-400';
    return '';
  }

  getCriticalEventIconClass(count: number): string {
    if (count > 0) return 'bg-red-50 dark:bg-red-500/15 text-red-700';
    return 'bg-stone-100 dark:bg-stone-700 text-stone-600 dark:text-stone-300';
  }

  getCriticalEventValueClass(count: number): string {
    if (count > 0) return 'text-red-700 dark:text-red-400';
    return '';
  }

  getSystemHealthIconClass(health: string): string {
    switch (health) {
      case 'healthy': return 'bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400';
      case 'warning': return 'bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400';
      case 'critical': return 'bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-400';
      default: return 'bg-stone-100 dark:bg-stone-700 text-stone-600 dark:text-stone-300';
    }
  }

  getSystemHealthValueClass(health: string): string {
    switch (health) {
      case 'healthy': return 'text-green-700 dark:text-green-400';
      case 'warning': return 'text-amber-700 dark:text-amber-400';
      case 'critical': return 'text-red-700 dark:text-red-400';
      default: return '';
    }
  }

  private isUnusualIP(ipAddress: string): boolean {
    // Simple check for private IP ranges (consider these less risky)
    if (ipAddress.startsWith('192.168.') || 
        ipAddress.startsWith('10.') || 
        ipAddress.startsWith('172.')) {
      return false;
    }
    // Add more sophisticated IP analysis here
    return false;
  }

  private isUnusualUserAgent(userAgent: string): boolean {
    // Check for automated tools, scrapers, etc.
    const suspiciousPatterns = [
      'bot', 'crawler', 'spider', 'scraper', 'python', 'curl', 'wget'
    ];
    
    return suspiciousPatterns.some(pattern => 
      userAgent.toLowerCase().includes(pattern)
    );
  }

  // Terminate a user session
  terminateSession(sessionId: string): void {
    if (confirm('Are you sure you want to terminate this session?')) {
      this.securityService.invalidateSession(sessionId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            alert('Session terminated successfully');
            this.loadActiveSessions(); // Reload sessions
          },
          error: (error: any) => {
            console.error('Failed to terminate session:', error);
            alert('Failed to terminate session');
          }
        });
    }
  }

  // Get severity badge class (design-system Tailwind classes)
  getSeverityClass(severity: string): string {
    switch (severity.toLowerCase()) {
      case 'critical':
      case 'high':
        return 'bg-red-50 text-red-700 ring-1 ring-red-200';
      case 'medium':
        return 'bg-amber-50 text-amber-800 ring-1 ring-amber-200';
      case 'low':
      case 'info':
        return 'bg-blue-50 text-blue-700 ring-1 ring-blue-200';
      default:
        return 'bg-stone-100 text-stone-700 ring-1 ring-stone-200';
    }
  }

  // Get event type display name
  getEventTypeDisplay(eventType: string): string {
    return eventType.replace(/_/g, ' ').toLowerCase()
      .replace(/\b\w/g, (l: string) => l.toUpperCase());
  }

  // Format time difference
  getTimeAgo(date: string): string {
    const now = new Date();
    const eventDate = new Date(date);
    const diffMs = now.getTime() - eventDate.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMins > 0) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }

  // Filter events by type
  onEventTypeChange(): void {
    this.loadRecentEvents();
  }

  // Filter events by severity
  onSeverityChange(): void {
    this.loadRecentEvents();
  }

  // Export security audit report
  exportAuditReport(): void {
    const today = new Date();
    const startDate = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000)); // 30 days ago
    
    this.securityService.exportSecurityEvents(
      startDate.toISOString(),
      today.toISOString()
    ).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // Create download link
          const blob = new Blob([response], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `security-audit-${today.toISOString().split('T')[0]}.csv`;
          link.click();
          window.URL.revokeObjectURL(url);
        },
        error: (error: any) => {
          console.error('Failed to export audit report:', error);
          alert('Failed to export audit report');
        }
      });
  }

  // Helper methods for template calculations
  getHighRiskSessionsCount(): number {
    return this.getFilteredSessions().filter(session => this.getSessionRiskLevel(session) === 'high').length;
  }

  getMediumRiskSessionsCount(): number {
    return this.getFilteredSessions().filter(session => this.getSessionRiskLevel(session) === 'medium').length;
  }

  getLowRiskSessionsCount(): number {
    return this.getFilteredSessions().filter(session => this.getSessionRiskLevel(session) === 'low').length;
  }
}




